import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Tag, Plus, Trash2, Edit2, Save, Lock, Unlock, Loader2,
  AlertTriangle, Percent, DollarSign,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  if (typeof d === "object") return d.message_ar || d.message_en || d.msg || JSON.stringify(d);
  return String(d);
};

export default function DiscountPolicySettings() {
  const { lang, t } = useLanguage();
  const isAr = lang === "ar";
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();

  const [policy, setPolicy] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pinInput, setPinInput] = useState("");

  // Reason editing
  const [newReason, setNewReason] = useState({ label_ar: "", label_en: "" });
  const [editingReason, setEditingReason] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get("/pos/discount-policy"),
        api.get("/discount-reasons"),
      ]);
      setPolicy(p.data);
      setReasons(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePolicy = async () => {
    setSaving(true);
    try {
      const body = {
        enabled: policy.enabled,
        cashier_max_percent: policy.cashier_max_percent,
        cashier_max_amount: policy.cashier_max_amount,
        require_reason: policy.require_reason,
        allow_with_global_discount: policy.allow_with_global_discount,
      };
      // PIN: only send when user actually typed a new one or cleared
      if (pinInput === "__CLEAR__") body.manager_pin = "";
      else if (pinInput) body.manager_pin = pinInput;
      const r = await api.put("/pos/discount-policy", body);
      setPolicy(r.data); setPinInput("");
      toast.success(isAr ? "تم الحفظ" : "Saved");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const addReason = async () => {
    if (!newReason.label_ar || !newReason.label_en) {
      toast.error(isAr ? "اكتب السبب بالعربي والإنجليزي" : "Both labels required");
      return;
    }
    try {
      const r = await api.post("/discount-reasons", { ...newReason, active: true });
      setReasons((x) => [...x, r.data]);
      setNewReason({ label_ar: "", label_en: "" });
      toast.success(isAr ? "أُضيف" : "Added");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const updateReason = async (r) => {
    try {
      const res = await api.put(`/discount-reasons/${r.reason_id}`, {
        label_ar: r.label_ar, label_en: r.label_en, active: r.active,
      });
      setReasons((arr) => arr.map((x) => x.reason_id === r.reason_id ? res.data : x));
      setEditingReason(null);
      toast.success(isAr ? "حُدّث" : "Updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteReason = async (id) => {
    const ok = await confirm({
      title: isAr ? "حذف هذا السبب؟" : "Delete this reason?",
      confirmLabel: isAr ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/discount-reasons/${id}`);
      setReasons((x) => x.filter((r) => r.reason_id !== id));
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading || !policy) {
    return <div className="card-surface p-10 text-center"><Loader2 className="inline animate-spin" size={24}/></div>;
  }

  return (
    <div className="space-y-5" data-testid="discount-policy-panel">
      {/* Header */}
      <div className="card-surface p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-xl text-[var(--text)] flex items-center gap-2">
              <Tag size={20} className="text-amber-600"/>
              {isAr ? "خصومات نقطة البيع" : "POS Discount Policy"}
            </h3>
            <p className="text-xs text-[var(--text-muted)] mt-1 max-w-2xl">
              {isAr
                ? "اضبط حدود الخصم لكل بند في الفاتورة، أسباب الخصم، و PIN اعتماد المدير."
                : "Configure per-line discount caps, customizable reasons, and the manager-PIN override flow."}
            </p>
          </div>
          <button onClick={savePolicy} disabled={saving} data-testid="dp-save-btn"
            className="btn-gold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
            {isAr ? "حفظ" : "Save"}
          </button>
        </div>

        {/* Master toggle */}
        <div className="mt-5 border border-stone-200 rounded-lg p-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">{isAr ? "تفعيل خصم البنود" : "Enable per-line discounts"}</div>
            <div className="text-xs text-stone-500 mt-1">
              {isAr ? "عند الإيقاف، يبقى الخصم العام فقط على إجمالي الفاتورة." : "When disabled, only the invoice-level global discount remains."}
            </div>
          </div>
          <Toggle checked={policy.enabled} onChange={(v) => setPolicy({ ...policy, enabled: v })}
                  testid="dp-enabled"/>
        </div>
      </div>

      {/* Caps */}
      <div className="card-surface p-5 space-y-4">
        <h4 className="font-semibold text-[var(--text)]">{isAr ? "حدود الكاشير" : "Cashier Caps"}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={isAr ? "حد أقصى للنسبة (%)" : "Max percent (%)"}
                 hint={isAr ? "أعلى نسبة يستطيع الكاشير منحها بدون PIN" : "Max % cashier can apply without manager PIN"}>
            <div className="flex items-center gap-2">
              <Percent size={14} className="text-amber-600"/>
              <input type="number" min="0" max="100" value={policy.cashier_max_percent}
                     data-testid="dp-max-pct"
                     onChange={(e) => setPolicy({ ...policy, cashier_max_percent: parseInt(e.target.value) || 0 })}
                     className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm num-display"/>
            </div>
          </Field>
          <Field label={isAr ? "حد أقصى للمبلغ (ر.ق)" : "Max amount (QAR)"}
                 hint={isAr ? "أعلى مبلغ بالريال يستطيع الكاشير منحه بدون PIN" : "Max QAR amount cashier can apply without manager PIN"}>
            <div className="flex items-center gap-2">
              <DollarSign size={14} className="text-amber-600"/>
              <input type="number" min="0" value={policy.cashier_max_amount}
                     data-testid="dp-max-amt"
                     onChange={(e) => setPolicy({ ...policy, cashier_max_amount: parseFloat(e.target.value) || 0 })}
                     className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm num-display"/>
            </div>
          </Field>
        </div>
        <div className="text-[11px] text-stone-500 leading-relaxed">
          {isAr
            ? "يتم تطبيق القاعدة الأكثر صرامة: إذا تجاوز الخصم أيًا من الحدين، سيحتاج الكاشير إلى PIN المدير."
            : "Whichever cap is breached first wins. Cashier needs manager PIN if either threshold is exceeded."}
        </div>
      </div>

      {/* Manager PIN */}
      <div className="card-surface p-5 space-y-3">
        <h4 className="font-semibold text-[var(--text)] flex items-center gap-2">
          {policy.manager_pin_set ? <Lock size={16} className="text-emerald-600"/> : <Unlock size={16} className="text-stone-400"/>}
          {isAr ? "PIN المدير" : "Manager PIN"}
        </h4>
        {policy.manager_pin_set ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
            ✓ {isAr ? "PIN مضبوط — اعتماد المدير مطلوب لتجاوز حدود الكاشير." : "PIN configured. Cashier overrides require it."}
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
            <span>{isAr
              ? "لا يوجد PIN مضبوط. الكاشير يستطيع تطبيق أي خصم. اضبط PIN لتفعيل اعتماد المدير."
              : "No PIN set — cashier can apply any discount. Set a PIN to enforce manager approval."}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <input type="password" inputMode="numeric"
                 placeholder={isAr ? "PIN جديد (4-8 أرقام)" : "New PIN (4-8 digits)"}
                 value={pinInput === "__CLEAR__" ? "" : pinInput}
                 onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 8))}
                 data-testid="dp-new-pin"
                 className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm font-mono text-center tracking-widest"/>
          {policy.manager_pin_set && (
            <button onClick={() => { setPinInput("__CLEAR__"); toast.info(isAr ? "سيُحذف عند الحفظ" : "Will be cleared on Save"); }}
                    data-testid="dp-clear-pin"
                    className="px-3 py-2 rounded-lg text-xs border border-rose-200 text-rose-600 hover:bg-rose-50">
              {isAr ? "حذف الـ PIN" : "Clear PIN"}
            </button>
          )}
        </div>
        {pinInput === "__CLEAR__" && <div className="text-xs text-rose-600">{isAr ? "سيُحذف PIN عند الضغط على «حفظ»" : "PIN will be cleared on save"}</div>}
      </div>

      {/* Behavior switches */}
      <div className="card-surface p-5 space-y-3">
        <h4 className="font-semibold text-[var(--text)]">{isAr ? "سلوك إضافي" : "Behavior"}</h4>
        <div className="border border-stone-200 rounded-lg p-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">{isAr ? "إجبار الكاشير على اختيار سبب" : "Force cashier to pick a reason"}</div>
            <div className="text-xs text-stone-500 mt-1">{isAr ? "لا يقبل أي خصم بدون سبب من القائمة." : "Reject any line discount without a reason."}</div>
          </div>
          <Toggle checked={policy.require_reason} onChange={(v) => setPolicy({ ...policy, require_reason: v })}
                  testid="dp-require-reason"/>
        </div>
        <div className="border border-stone-200 rounded-lg p-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">{isAr ? "السماح بدمج خصم البنود مع خصم الفاتورة" : "Allow combining with invoice-level discount"}</div>
            <div className="text-xs text-stone-500 mt-1">{isAr ? "عند الإيقاف، لا يمكن للكاشير استخدام النوعين معًا." : "Block applying both line + global discount on the same invoice."}</div>
          </div>
          <Toggle checked={policy.allow_with_global_discount}
                  onChange={(v) => setPolicy({ ...policy, allow_with_global_discount: v })}
                  testid="dp-allow-combo"/>
        </div>
      </div>

      {/* Reasons CRUD */}
      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-[var(--text)]">{isAr ? "أسباب الخصم" : "Discount Reasons"}</h4>
          <span className="text-xs text-stone-500">{reasons.length} {isAr ? "سبب" : "items"}</span>
        </div>

        {/* Add row */}
        <div className="grid grid-cols-12 gap-2 mb-3">
          <input value={newReason.label_ar} onChange={(e) => setNewReason({ ...newReason, label_ar: e.target.value })}
                 data-testid="dp-new-reason-ar"
                 placeholder={isAr ? "العنوان بالعربي" : "Arabic label"}
                 className="col-span-5 border border-stone-200 rounded-lg px-3 py-2 text-sm"/>
          <input value={newReason.label_en} onChange={(e) => setNewReason({ ...newReason, label_en: e.target.value })}
                 data-testid="dp-new-reason-en"
                 placeholder={isAr ? "العنوان بالإنجليزي" : "English label"}
                 className="col-span-5 border border-stone-200 rounded-lg px-3 py-2 text-sm"/>
          <button onClick={addReason} data-testid="dp-add-reason"
                  className="col-span-2 btn-gold rounded-lg flex items-center justify-center gap-1 text-sm">
            <Plus size={14}/>{isAr ? "إضافة" : "Add"}
          </button>
        </div>

        {/* Table */}
        {reasons.length === 0 ? (
          <div className="text-center py-8 text-sm text-stone-400">
            {isAr ? "لا توجد أسباب بعد. ابدأ بإضافة سبب من الأعلى." : "No reasons yet. Add one above."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-stone-500 uppercase">
              <tr>
                <th className="text-start py-2 px-2">{isAr ? "السبب (عربي)" : "Reason (AR)"}</th>
                <th className="text-start py-2 px-2">{isAr ? "السبب (إنجليزي)" : "Reason (EN)"}</th>
                <th className="text-start py-2 px-2">{isAr ? "فعّال" : "Active"}</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {reasons.map((r) => {
                const isEditing = editingReason?.reason_id === r.reason_id;
                const draft = isEditing ? editingReason : r;
                return (
                  <tr key={r.reason_id} className="border-t border-stone-100" data-testid={`dp-reason-row-${r.reason_id}`}>
                    <td className="py-2 px-2">
                      {isEditing ? (
                        <input value={draft.label_ar}
                               onChange={(e) => setEditingReason({ ...editingReason, label_ar: e.target.value })}
                               className="border border-stone-200 rounded px-2 py-1 text-sm w-full"/>
                      ) : <span>{r.label_ar}</span>}
                    </td>
                    <td className="py-2 px-2">
                      {isEditing ? (
                        <input value={draft.label_en}
                               onChange={(e) => setEditingReason({ ...editingReason, label_en: e.target.value })}
                               className="border border-stone-200 rounded px-2 py-1 text-sm w-full"/>
                      ) : <span>{r.label_en}</span>}
                    </td>
                    <td className="py-2 px-2">
                      <Toggle checked={draft.active !== false}
                              onChange={(v) => {
                                if (isEditing) setEditingReason({ ...editingReason, active: v });
                                else updateReason({ ...r, active: v });
                              }}/>
                    </td>
                    <td className="py-2 px-2 text-end">
                      <div className="inline-flex gap-1">
                        {isEditing ? (
                          <button onClick={() => updateReason(editingReason)} className="p-1 rounded hover:bg-emerald-50 text-emerald-600" title="Save"><Save size={14}/></button>
                        ) : (
                          <button onClick={() => setEditingReason(r)} className="p-1 rounded hover:bg-stone-100 text-stone-500" title="Edit"><Edit2 size={14}/></button>
                        )}
                        <button onClick={() => deleteReason(r.reason_id)} className="p-1 rounded hover:bg-rose-50 text-rose-600" title="Delete"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <ConfirmDialogSlot />
    </div>
  );
}

// ── Helpers ──
const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-xs text-stone-500 mb-1 block">{label}</label>
    {children}
    {hint && <div className="text-[10px] text-stone-400 mt-1 leading-relaxed">{hint}</div>}
  </div>
);

const Toggle = ({ checked, onChange, testid }) => (
  <label className="relative inline-flex items-center cursor-pointer shrink-0">
    <input type="checkbox" className="sr-only peer" checked={checked}
           data-testid={testid}
           onChange={(e) => onChange(e.target.checked)}/>
    <div className="w-11 h-6 bg-stone-200 peer-checked:bg-emerald-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5 rtl:peer-checked:after:-translate-x-5"/>
  </label>
);
